/**
 * Creating a reproduction: name it and upload its input file.
 *
 * One component for all three kinds; what differs (the endpoint, the wording,
 * the accepted file types) comes from the reproduction-kind descriptor rather
 * than from branches here.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Stack, Button, Typography, Box, TextField, Alert, Checkbox, FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import NavBarBottom from "../../components/NavBarBottom";
import { REPRODUCTION_KINDS, REPRODUCTIONS_BASE, reproductionRoute } from "../../helper/reproductionFlow";
import { projectIdFromSearch } from "../../helper/projects";

// Create page of one reproduction kind (/colorreproductions/<kind>/new): name
// + file upload with a safe <img> preview. Next creates the reproduction on
// the server and continues to its Set up step; Back returns to the list.
export default function ColorReproductionNew({ kind = "logo" }) {
  const navigate = useNavigate();
  // Started from a project's New menu: the reproduction is filed there.
  const location = useLocation();
  const projectId = projectIdFromSearch(location.search);
  const reproductionKind = REPRODUCTION_KINDS[kind] || REPRODUCTION_KINDS.logo;
  // Back: the combined list of every kind.
  const listRoute = REPRODUCTIONS_BASE;

  const [newName, setNewName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState("");
  const uploadPreviewUrlRef = useRef("");
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // Logos only: whether size and position refer to the drawn objects or to
  // the whole SVG canvas - a property of the file, fixed with the upload.
  const isLogo = reproductionKind.type === "LOGO_REPRODUCTION";
  const [trimWhitespace, setTrimWhitespace] = useState(false);

  useEffect(() => {
    return () => {
      // Release the upload preview object URL when leaving the page.
      if (uploadPreviewUrlRef.current) URL.revokeObjectURL(uploadPreviewUrlRef.current);
    };
  }, []);

  const setPreviewUrl = (url) => {
    if (uploadPreviewUrlRef.current) URL.revokeObjectURL(uploadPreviewUrlRef.current);
    uploadPreviewUrlRef.current = url;
    setUploadPreviewUrl(url);
  };

  const handleFilePicked = (file) => {
    if (!file) return;
    // Validate by extension: Windows often reports an empty MIME type.
    const lowerName = file.name.toLowerCase();
    if (!reproductionKind.extensions.some((extension) => lowerName.endsWith(extension))) {
      setErrorMessage(reproductionKind.extensionError);
      return;
    }
    setErrorMessage(null);
    setUploadFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Creates the reproduction and opens its Set up step (its id is in the
  // URL from here on).
  const createReproduction = async () => {
    if (!newName.trim() || !uploadFile) return;
    setCreating(true);
    setErrorMessage(null);
    try {
      const formData = new FormData();
      formData.append(reproductionKind.field, uploadFile);
      formData.append("name", newName.trim());
      if (isLogo) formData.append("trim_content_whitespace", trimWhitespace ? "true" : "false");
      if (projectId) formData.append("project", projectId);
      const response = await axios.post(reproductionKind.endpoint, formData);
      navigate(reproductionRoute(reproductionKind.type, response.data.reproduction_id, "setup"));
    } catch (error) {
      console.error("Error creating reproduction:", error);
      const detail = error?.response?.data?.error;
      setErrorMessage(detail || "Creating the reproduction failed.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title={reproductionKind.newTitle}
        subtitle={reproductionKind.newHint}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={listRoute}
              enableNextPage={!newName.trim() || !uploadFile}
              nextLoading={creating}
              backDisabledWhenNextLoading
              nextPageClickFunc={createReproduction}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "flex-start" }}>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                label="Reproduction name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                disabled={creating}
              />
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  disabled={creating}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  {reproductionKind.chooseLabel}
                  <input
                    hidden
                    accept={reproductionKind.extensions.join(",")}
                    type="file"
                    onChange={(event) => {
                      handleFilePicked(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </Button>
                {uploadFile ? (
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                    {uploadFile.name}
                  </Typography>
                ) : null}
              </Stack>
              {isLogo ? (
                <Stack spacing={0}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={trimWhitespace}
                        disabled={creating}
                        onChange={(event) => setTrimWhitespace(event.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Prune whitespace around the objects</Typography>}
                    sx={{ ml: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                    Checked: height and position apply to the drawn objects. Unchecked: to the whole SVG canvas.
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
            <Box
              sx={{
                width: 180, flexShrink: 0, aspectRatio: "1",
                borderRadius: 2, overflow: "hidden",
                border: "1px dashed",
                borderColor: uploadPreviewUrl ? "divider" : "text.disabled",
                bgcolor: "grey.50",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {uploadPreviewUrl ? (
                <img
                  src={uploadPreviewUrl}
                  alt={reproductionKind.previewAlt}
                  style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                  {reproductionKind.previewHint}
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
      </ExplorationPageShell>
    </Stack>
  );
}
